// Copyright (c) StrangeBee 2023

import { findUser } from '@Helpers/findUser';
import { useLocalMessage } from '@Helpers/locale';
import { LanguageSwitcherContext } from '@Lang/LanguageSwitcherContext';
import { MessageProps, Comments } from '@Molecules/Comments/Comments';
import { useListAllUsers } from '@Queries/userQueries';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState, ReactElement } from 'react';
import { CommentOutput } from 'thehive-sdk';

interface CommentsListProps {
    data: CommentOutput[];
    onLoadPage?: (n: number) => void;
    showLoad?: boolean;
    onEdit?: (txt: string, id: string) => void;
    onDelete?: (id: string) => void;
    readOnly?: boolean;
    currentUserLogin?: string;
}

export const CommentsList = ({
    data,
    onLoadPage,
    onEdit,
    onDelete,
    showLoad = true,
    readOnly = true,
    currentUserLogin = '',
}: CommentsListProps): ReactElement => {
    const local = useLocalMessage();
    const [page, setPage] = useState(0);
    const [dataTreat, setDataTreat] = useState<MessageProps[]>([]);
    const { language } = useContext(LanguageSwitcherContext);

    const { data: userList = [] } = useListAllUsers();

    const addMessage = useCallback(
        (newData: CommentOutput[], oldData = [] as MessageProps[]): MessageProps[] => {
            return newData.reduce((acc, current: CommentOutput): MessageProps[] => {
                const { name = '', avatar = '' } = findUser(current.createdBy, userList) || {};

                const currentTreat = {
                    objectId: current._id,
                    user: name,
                    date: moment(current.createdAt).locale(language).fromNow(),
                    message: current.message,
                    avatar,
                    isMine: current.createdBy === currentUserLogin,
                    readOnly,
                };

                const option = current.updatedAt
                    ? { edited: moment(current.updatedAt).locale(language).fromNow() }
                    : {};

                return [{ ...currentTreat, ...option }, ...acc];
            }, oldData as MessageProps[]);
        },
        [userList, currentUserLogin, readOnly, language],
    );

    useEffect(() => {
        setDataTreat(addMessage(data, []));
    }, [data, userList]); //eslint-disable-line

    useEffect(() => {
        onLoadPage?.(page);
    }, [page, onLoadPage]);

    return (
        <Comments
            data={dataTreat}
            onEdit={onEdit}
            onDelete={onDelete}
            moreConfig={{
                message: local('comment.more'),
                show: !!(showLoad && onLoadPage),
                onClick: () => {
                    setPage((p) => p + 1);
                },
            }}
        />
    );
};
